/**
 * Alta de albarán.
 *
 * Un albarán documenta lo que se entregó y se hizo en un sitio. Su valor
 * probatorio depende de una sola cosa: **la firma del cliente**. Por eso
 * la pantalla lo dice al crear, en lugar de dejar que se descubra al
 * intentar facturar.
 *
 * Los tiempos —desplazamiento y trabajo— no son decorativos: alimentan la
 * rentabilidad por orden y, en los contratos de horas y materiales, lo que
 * se factura.
 */

import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from '../../forms/use-form.js';
import { FormSection, TextArea, TextField } from '../../forms/fields.js';
import { EntityPicker } from '../../forms/entity-picker.js';
import { LineEditor, TotalsSummary, emptyLine, useLineTotals, type EditableLine } from '../../forms/line-editor.js';
import { isoDate, type FieldRules } from '../../forms/form-state.js';

const RULES: FieldRules = {
  deliveredOn: [isoDate()],
};

export function DeliveryNoteFormScreen(): React.ReactNode {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const counter = useRef(1);
  const nextId = (): string => `l${counter.current++}`;

  const [customerId, setCustomerId] = useState('');
  const [siteId, setSiteId] = useState(params.get('siteId') ?? '');
  const [customerError, setCustomerError] = useState<string | undefined>(undefined);
  const [lines, setLines] = useState<EditableLine[]>([emptyLine(nextId())]);
  const [lineError, setLineError] = useState<string | null>(null);

  const form = useForm<{ id: string; number: string }>({
    mode: 'create',
    nullable: ['siteId', 'workOrderId', 'technicianId', 'travelMinutes', 'workMinutes'],
    rules: RULES,
    initial: {
      deliveredOn: new Date().toISOString().slice(0, 10),
      travelMinutes: '',
      workMinutes: '',
      observations: '',
    },
    onSubmit: async (body, client) => {
      const payload: Record<string, unknown> = {
        ...body,
        customerId,
        siteId: siteId === '' ? null : siteId,
        lines: lines
          .filter((line) => line.description.trim() !== '')
          .map((line) => ({
            kind: line.kind,
            description: line.description.trim(),
            quantity: line.kind === 'TEXTO' ? '0' : line.quantity.replace(',', '.'),
            unitPrice: line.kind === 'TEXTO' ? '0.00' : line.unitPrice.replace(',', '.'),
            discountRate: line.discountRate.trim() === '' ? null : line.discountRate.replace(',', '.'),
            taxRate: line.kind === 'TEXTO' ? '0' : line.taxRate,
          })),
      };

      for (const key of ['travelMinutes', 'workMinutes']) {
        if (typeof payload[key] === 'string' && payload[key] !== '') {
          payload[key] = Number(payload[key]);
        }
      }

      return client.post<{ id: string; number: string }>('/delivery-notes', payload);
    },
    onSaved: () => void navigate('/albaranes'),
  });

  const totals = useLineTotals(lines, '');

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();

    if (customerId === '') {
      setCustomerError('Elija el cliente.');
      return;
    }
    setCustomerError(undefined);

    if (lines.filter((line) => line.description.trim() !== '').length === 0) {
      setLineError('Un albarán necesita al menos una línea con concepto.');
      return;
    }
    if (totals.invalidLines.length > 0) {
      setLineError('Hay líneas con números que no se pueden interpretar.');
      return;
    }
    setLineError(null);

    await form.submit();
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Nuevo albarán</h1>
        {form.dirty && <span className="screen__count">Cambios sin guardar</span>}
      </header>

      <p className="screen__note">
        El albarán se crea sin firma. <strong>Sin la firma del cliente no vale como prueba de
        entrega</strong>, y se recoge en el propio centro —en la app de campo— o desde la ficha del
        albarán.
      </p>

      {(form.formError !== null || lineError !== null) && (
        <p className="screen__error" role="alert">
          {lineError ?? form.formError}
        </p>
      )}

      {form.orphanErrors.length > 0 && (
        <p className="screen__error" role="alert">
          El servidor ha rechazado campos que este formulario no muestra:{' '}
          {form.orphanErrors.join(' · ')}
        </p>
      )}

      <form className="form" onSubmit={(e) => void submit(e)} noValidate>
        <FormSection title="Entrega">
          <EntityPicker
            label="Cliente"
            entity="CUSTOMER"
            value={customerId}
            onChange={(v) => {
              setCustomerId(v);
              setCustomerError(undefined);
            }}
            error={customerError}
            required
          />
          <EntityPicker label="Centro" entity="SITE" value={siteId} onChange={setSiteId} />
          <TextField
            label="Fecha de entrega"
            type="date"
            value={String(form.values['deliveredOn'] ?? '')}
            onChange={(v) => form.set('deliveredOn', v)}
            onBlur={() => form.blur('deliveredOn')}
            error={form.errors['deliveredOn']}
            required
          />
        </FormSection>

        <FormSection title="Líneas">
          <div className="form__full">
            <LineEditor lines={lines} onChange={setLines} totals={totals} nextId={nextId} />
          </div>
          <div className="form__full">
            <TotalsSummary totals={totals} />
          </div>
        </FormSection>

        <FormSection
          title="Tiempos"
          description="Alimentan la rentabilidad por orden y, en los contratos de horas y materiales, lo que se factura."
        >
          <TextField
            label="Desplazamiento (min)"
            type="number"
            value={String(form.values['travelMinutes'] ?? '')}
            onChange={(v) => form.set('travelMinutes', v)}
          />
          <TextField
            label="Trabajo (min)"
            type="number"
            value={String(form.values['workMinutes'] ?? '')}
            onChange={(v) => form.set('workMinutes', v)}
          />
          <TextArea
            label="Observaciones"
            value={String(form.values['observations'] ?? '')}
            onChange={(v) => form.set('observations', v)}
          />
        </FormSection>

        <footer className="form__actions">
          <button className="button button--primary" type="submit" disabled={form.saving}>
            {form.saving ? 'Creando…' : 'Crear albarán'}
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              if (form.dirty && !window.confirm('Hay cambios sin guardar. ¿Salir igualmente?')) return;
              void navigate('/albaranes');
            }}
          >
            Volver
          </button>
        </footer>
      </form>
    </section>
  );
}
